/**
 * Surfaces a failed or wedged planning run so the UI can stop pretending work
 * is still in flight.
 *
 * Two things can leave the progress tracker spinning forever:
 *
 *  1. The run ends with a failure status. `planning_complete` carries it, but
 *     every terminal status other than `cancelled` was treated as a clean
 *     finish, so an errored run looked identical to a successful one.
 *  2. The run dies without emitting any terminal event at all — a provider that
 *     keeps timing out, or a worker that stops making progress. Nothing ever
 *     clears `is_active`, so the spinner runs until the page is reloaded.
 *
 * Case 2 is why this watches elapsed time rather than trusting events alone:
 * silence is the failure signal, and only a clock can observe silence.
 */
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { CLIENT_TIMEOUT_DEFAULTS, getClientTimeout } from '../services/clientTimeoutConfig';
import { eventService } from '../services/eventService';

/** Statuses that mean the run stopped without delivering its work. */
const FAILURE_STATUSES = new Set(['error', 'failed', 'planning_failed']);

/**
 * Events that prove the backend is still *making progress* on this run.
 *
 * Deliberately excludes `ai_overview_text`: the Tech Lead cheerleader emits it
 * every ~15s specifically to fill idle gaps, so it keeps arriving long after
 * real work has stopped. Counting it as progress would make this watchdog blind
 * to exactly the wedged runs it exists to catch. Only state transitions count.
 */
const PROGRESS_EVENTS = [
  'agent_log',
  'design_pages',
  'agent_state_restore',
  'build_phases_update',
  'task_update',
] as const;

/**
 * How long a run may go without a state transition before it counts as wedged.
 * Generous on purpose: an observed page repair plus its quality retries ran
 * about four minutes, so this leaves roughly 2.5x headroom. A false "it failed"
 * is worse than a late one. This is the default; the admin timeout panel can
 * change it, and the watchdog reads the configured value each time it arms.
 */
export const PLANNING_STALL_TIMEOUT_MS = CLIENT_TIMEOUT_DEFAULTS.planningStallTimeoutMs;

const STALL_MESSAGE = 'Planning stopped responding. The last step never reported back.';
const DEFAULT_FAILURE_MESSAGE = 'Planning failed before it could finish.';

export interface PlanningRunFailure {
  /** User-facing reason, or null while the run is healthy. */
  message: string | null;
  /** Clears the banner and re-arms the watchdog. */
  dismiss: () => void;
}

interface PlanningCompleteEvent {
  project_id?: string | null;
  status?: string | null;
  error?: string | null;
}

type SetMessage = Dispatch<SetStateAction<string | null>>;

const isForProject = (data: unknown, projectId: string | null): boolean => {
  const payload = data as PlanningCompleteEvent | null;

  if (!payload || typeof payload !== 'object') return false;

  // A payload without a project id predates per-project scoping. The SSE stream
  // is already per-project, so treat it as in scope rather than dropping it.
  return !payload.project_id || payload.project_id === projectId;
};

/** Raise a banner when the run reports a terminal failure status. */
const useTerminalFailure = (projectId: string | null, setMessage: SetMessage): void => {
  useEffect(() => {
    if (!projectId) return undefined;

    return eventService.subscribe('planning_complete', (data: unknown) => {
      if (!isForProject(data, projectId)) return;

      const payload = data as PlanningCompleteEvent;
      const status = (payload.status || '').toLowerCase();

      // A non-failure terminal event means the run resolved after all, so it
      // also clears a stall banner raised moments earlier by a slow-but-alive run.
      // A second failure event without a reason keeps the reason already shown.
      setMessage((current) => (FAILURE_STATUSES.has(status)
        ? payload.error?.trim() || current || DEFAULT_FAILURE_MESSAGE
        : null));
    });
  }, [projectId, setMessage]);
};

/** Track when this project last showed any sign of life. */
const useProgressHeartbeat = (
  projectId: string | null,
  lastProgressRef: MutableRefObject<number>,
): void => {
  useEffect(() => {
    if (!projectId) return undefined;

    const unsubscribers = PROGRESS_EVENTS.map((eventType) => eventService.subscribe(
      eventType,
      (data: unknown) => {
        if (!isForProject(data, projectId)) return;
        lastProgressRef.current = Date.now();
      },
    ));

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [projectId, lastProgressRef]);
};

/**
 * Report a run that has gone quiet for too long. Armed only while running. The
 * stall timeout and the check interval are the panel values in effect when it arms.
 */
const useStallWatchdog = (
  projectId: string | null,
  isRunning: boolean,
  armToken: number,
  lastProgressRef: MutableRefObject<number>,
  setMessage: SetMessage,
): void => {
  useEffect(() => {
    if (!projectId || !isRunning) return undefined;

    lastProgressRef.current = Date.now();
    const stallTimeoutMs = getClientTimeout('planningStallTimeoutMs');
    const timer = globalThis.setInterval(() => {
      if (Date.now() - lastProgressRef.current >= stallTimeoutMs) {
        setMessage((current) => current ?? STALL_MESSAGE);
      }
    }, getClientTimeout('planningStallCheckIntervalMs'));

    return () => globalThis.clearInterval(timer);
  }, [projectId, isRunning, armToken, lastProgressRef, setMessage]);
};

/** Message plus the controls the signal hooks need, scoped to one project. */
const usePlanningFailureState = (projectId: string | null) => {
  // Keyed by project so switching projects drops the previous run's failure
  // during render, rather than through an effect that fires a second render.
  const [failure, setFailure] = useState<{ projectId: string | null; message: string | null }>(
    { projectId, message: null },
  );
  const [armToken, setArmToken] = useState(0);
  const lastProgressRef = useRef<number>(0);

  const setMessage: SetMessage = useCallback((value) => {
    setFailure((current) => {
      const scoped = current.projectId === projectId ? current.message : null;

      return {
        projectId,
        message: typeof value === 'function' ? value(scoped) : value,
      };
    });
  }, [projectId]);

  const dismiss = useCallback(() => {
    setMessage(null);
    setArmToken((token) => token + 1);
  }, [setMessage]);

  return {
    message: failure.projectId === projectId ? failure.message : null,
    setMessage,
    dismiss,
    armToken,
    lastProgressRef,
  };
};

/**
 * @param projectId Project whose run is being watched.
 * @param isRunning Whether the UI currently believes a run is in flight. The
 *                  watchdog arms only while this is true, so an idle project
 *                  never trips it.
 */
export function usePlanningRunFailure(
  projectId: string | null,
  isRunning: boolean,
): PlanningRunFailure {
  const {
    message, setMessage, dismiss, armToken, lastProgressRef,
  } = usePlanningFailureState(projectId);

  useTerminalFailure(projectId, setMessage);
  useProgressHeartbeat(projectId, lastProgressRef);
  useStallWatchdog(projectId, isRunning, armToken, lastProgressRef, setMessage);

  return { message, dismiss };
}
